import { useCallback, useEffect, useState } from "react";
import { ICustomerDeliveryAddress, IDeliveryAddressShippingCost } from "../common/interfaces";
import { DeliveryAddressService } from "../services/DeliveryAddressService";

/**
 * Direcciones de entrega guardadas de un cliente, con su costo de envío
 * cacheado por sucursal (warehouse_id). Reusado por orderOptions.tsx.
 */
export const useCustomerDeliveryAddresses = (customerId?: number) => {
  const [addresses, setAddresses] = useState<ICustomerDeliveryAddress[]>([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(() => {
    if (!customerId) {
      setAddresses([]);
      return;
    }
    setLoading(true);
    DeliveryAddressService.getByCustomer(customerId)
      .then((res) => {
        setAddresses((res.data ?? []) as ICustomerDeliveryAddress[]);
      })
      .finally(() => setLoading(false));
  }, [customerId]);

  useEffect(() => {
    load();
  }, [load]);

  const createAddress = async (payload: {
    name: string;
    address: string;
    number?: string;
    lat?: number;
    lng?: number;
  }): Promise<ICustomerDeliveryAddress | undefined> => {
    if (!customerId) return undefined;
    const res = await DeliveryAddressService.create({ ...payload, customer_id: customerId });
    load();
    return res.data;
  };

  const cacheShippingCost = async (
    addressId: number,
    warehouseId: number,
    rawShippingCost: number,
    distanceMeters: number
  ): Promise<IDeliveryAddressShippingCost | undefined> => {
    const res = await DeliveryAddressService.upsertShippingCost(addressId, {
      warehouse_id: warehouseId,
      shipping_cost: rawShippingCost,
      distance_meters: distanceMeters,
    });
    load();
    return res.data;
  };

  /**
   * Precio BASE cacheado (sin el descuento de envío gratis) + la distancia
   * usada para calcularlo. El descuento se aplica aparte con el total de la
   * orden ACTUAL (ver useShippingCalculator.applyFreeShippingDiscount) — no
   * es correcto cachear el precio ya descontado, porque ese descuento depende
   * del total de cada orden, no de la dirección/sucursal.
   */
  const getCachedCost = (
    address: ICustomerDeliveryAddress,
    warehouseId: number
  ): { rawPrice: number; distanceMeters: number } | undefined => {
    const cost = address.shipping_costs?.find((c) => c.warehouse_id === warehouseId);
    if (!cost || cost.distance_meters === undefined || cost.distance_meters === null) return undefined;
    // Laravel serializa columnas decimal/integer como string en algunos casos.
    return { rawPrice: Number(cost.shipping_cost), distanceMeters: Number(cost.distance_meters) };
  };

  return { addresses, loading, reload: load, createAddress, cacheShippingCost, getCachedCost };
};
